import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
    ArrowForward, CalendarToday, EmojiEvents, Edit, Groups, HourglassBottom, LockOutlined,
    MilitaryTech, Redeem, Search
} from "@mui/icons-material";
import { AuthContext } from "../../context/AuthContext";
import { admins, baseURL, currentSeason } from "../../Constants";
import {
    getFantasyLeaderboard, getMostPickedPlayers, getSeasons, getTopPicksByDivision,
    getTopSingleTeamPicks
} from "../../services/FantasyService";
import { useFantasyLock, formatLockCountdown, formatLockDate } from "../../hooks/useDeadlineLock";
import { getAllBadges, PlayerBadges, fantasyWinnerSeasons, isTopDonator, isVerifiedContent } from "../../services/BadgeService";
import { TopDonatorBadge } from "../../components/Badges/TopDonatorBadge";
import { VerifiedBadge } from "../../components/Badges/VerifiedBadge";
import { PremiumBadge } from "../../components/Badges/PremiumBadge";
import { DonatorBadge } from "../../components/Badges/DonatorBadge";
import { FantasyWinnerBadge } from "../../components/Badges/FantasyWinnerBadge";
import PlayerAvatar from "../../components/PlayerAvatar";
import PlayerImage from "../../components/PlayerImage";
import Loading from "../../components/Loading";
import { Account } from "../../models/Account";
import { FantasyPickDTO } from "../../models/FantasyPickDTO";
import { getSimpleDivisionLabel } from "../../utils/divisionUtils";
import PrizePanel from "./PrizePanel";
import { PRIZES, PRIZE_SEASON, prizeWinners } from "./prizes";
import styles from "./Fantasy2.module.css";

interface LeaderboardEntry {
    account: Account;
    points: number;
    placement: number;
    isCurrentUser: boolean;
}

type FantasyLeaderboardResponse = { [division: string]: LeaderboardEntry[] };
type Tab = 'leaderboard' | 'picks' | 'single';

const rankClass = (rank: number): string => {
    if (rank === 1) return styles.rankGold;
    if (rank === 2) return styles.rankSilver;
    if (rank === 3) return styles.rankBronze;
    return styles.rankDefault;
};

const rowClass = (i: number): string => (i % 2 === 0 ? styles.rowEven : styles.rowOdd);

// The Fantasy hub, served at /fantasy. Replaced the original hub at the season 33 launch;
// /fantasy2 redirects here.
// Consolidates the old /fantasy leaderboard, detailed-picks and best-picks pages into tabs.
const FantasyPage2 = () => {
    const { t, i18n } = useTranslation(['pages', 'common']);
    const { user: visitor } = useContext(AuthContext);

    const [tab, setTab] = useState<Tab>('leaderboard');
    const [loading, setLoading] = useState(true);
    const [seasons, setSeasons] = useState<number[]>([]);
    const [season, setSeason] = useState<number>(0);
    const [divisions, setDivisions] = useState<string[]>([]);
    const [division, setDivision] = useState<string | null>(null);
    const [leaderboard, setLeaderboard] = useState<FantasyLeaderboardResponse>({});
    const [mostPicked, setMostPicked] = useState<{ account: Account; picks: number }[]>([]);
    const [badges, setBadges] = useState<PlayerBadges | null>(null);

    // "Mest valgte" tab
    const [picksDivision, setPicksDivision] = useState<string>('all');
    const [picksSearch, setPicksSearch] = useState('');
    const [divisionPicks, setDivisionPicks] = useState<{ account: Account; picks: number }[]>([]);

    // "Bedste enkeltvalg" tab
    const [singleDivision, setSingleDivision] = useState<string | null>(null);
    const [singleSearch, setSingleSearch] = useState('');
    const [singlePicks, setSinglePicks] = useState<FantasyPickDTO[]>([]);

    const isAdmin = !!visitor && admins.includes(visitor.steamID);

    // Signups close automatically at the season deadline; the hook counts down to it live.
    const lock = useFantasyLock();
    const isLocked = lock.locked;
    const lockLocale = i18n.language.startsWith('da') ? 'da-DK' : 'en-GB';
    const lockDate = lock.lockTime ? formatLockDate(lock.lockTime, lockLocale) : '';
    const countdown = formatLockCountdown(lock.msRemaining ?? 0, {
        d: t('common:time.unitDay'),
        h: t('common:time.unitHour'),
        m: t('common:time.unitMinute'),
        s: t('common:time.unitSecond'),
    });

    useEffect(() => {
        getAllBadges().then(setBadges).catch(() => { });
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const list = await getSeasons();
                if (!list.includes(currentSeason)) list.push(currentSeason);
                list.sort((a, b) => b - a);
                setSeasons(list);
                setSeason((prev) => prev === 0 ? currentSeason : prev);
            } catch (err) {
                console.error("Error fetching seasons:", err);
            }
        })();
    }, []);

    const fetchLeaderboard = useCallback(async () => {
        if (season === 0) return;
        setLoading(true);
        try {
            const response = await getFantasyLeaderboard(season, visitor?.steamID ?? null);
            const transformed: FantasyLeaderboardResponse = Object.entries(response).reduce((acc, [div, teams]) => {
                acc[div] = (teams as any[]).map((team: any) => ({
                    account: team.account,
                    points: team.points,
                    placement: team.placement,
                    isCurrentUser: team.account.steamID === (visitor?.steamID ?? null),
                }));
                return acc;
            }, {} as FantasyLeaderboardResponse);
            setLeaderboard(transformed);

            const getOrder = (div: string) => {
                if (div === 'ligaen') return 0;
                const m = div.match(/^(\d)/);
                return m ? parseInt(m[1], 10) : 6;
            };
            const sorted = Object.keys(transformed).sort((a, b) => getOrder(a) - getOrder(b));
            setDivisions(sorted);
            setDivision((prev) => prev && sorted.includes(prev) ? prev : sorted[0] ?? null);
            setSingleDivision((prev) => prev && sorted.includes(prev) ? prev : sorted[0] ?? null);
        } catch (err) {
            console.error("Error fetching leaderboard:", err);
            setLeaderboard({});
            setDivisions([]);
        } finally {
            setLoading(false);
        }
    }, [season, visitor]);

    useEffect(() => {
        fetchLeaderboard();
    }, [fetchLeaderboard]);

    useEffect(() => {
        if (season === 0) return;
        getMostPickedPlayers(season).then(setMostPicked).catch(() => setMostPicked([]));
    }, [season]);

    // "Mest valgte" tab data: all divisions → popular picks (amount 0 = every picked player);
    // specific division → per-division picks
    useEffect(() => {
        if (season === 0 || tab !== 'picks') return;
        if (picksDivision === 'all') {
            getMostPickedPlayers(season, 0).then(setDivisionPicks).catch(() => setDivisionPicks([]));
        } else {
            getTopPicksByDivision(season, picksDivision).then(setDivisionPicks).catch(() => setDivisionPicks([]));
        }
    }, [season, picksDivision, tab]);

    useEffect(() => {
        if (season === 0 || tab !== 'single' || !singleDivision) return;
        getTopSingleTeamPicks(season, singleDivision, 10).then(setSinglePicks).catch(() => setSinglePicks([]));
    }, [season, singleDivision, tab]);

    // The visitor's best-placed team this season (for the placement chip + "Se dit hold")
    const myEntry = useMemo(() => {
        if (!visitor) return null;
        let best: { entry: LeaderboardEntry; division: string } | null = null;
        for (const [div, entries] of Object.entries(leaderboard)) {
            const mine = entries.find((e) => e.isCurrentUser);
            if (mine && (!best || mine.placement < best.entry.placement)) {
                best = { entry: mine, division: div };
            }
        }
        return best;
    }, [leaderboard, visitor]);

    // Which rows of the division on screen are currently in a prize position, and what they win.
    // Empty until somebody in the division has scored — see prizes.ts.
    const prizeByOwner = useMemo(() => {
        if (season !== PRIZE_SEASON) return new Map<string, string>();
        return new Map(prizeWinners(leaderboard, division).map((winner) => {
            const prize = PRIZES.find((p) => p.placement === winner.placement);
            return [winner.steamId, prize ? t(`pages:fantasy.${prize.key}`) : ''];
        }));
    }, [leaderboard, division, season, t]);

    const entries = division ? leaderboard[division] ?? [] : [];

    const filteredPicks = useMemo(() => {
        const q = picksSearch.trim().toLowerCase();
        const maxPicks = divisionPicks[0]?.picks || 1;
        return divisionPicks
            .filter((p) => !q || p.account.playerName?.toLowerCase().includes(q) || p.account.steamID.includes(q))
            .map((p) => ({ ...p, rank: divisionPicks.indexOf(p) + 1, pct: Math.round((p.picks / maxPicks) * 100) }));
    }, [divisionPicks, picksSearch]);

    const filteredSingle = useMemo(() => {
        const q = singleSearch.trim().toLowerCase();
        return singlePicks
            .map((p, i) => ({ ...p, rank: i + 1 }))
            .filter((p) => !q
                || p.playerAccount?.playerName?.toLowerCase().includes(q)
                || p.ownerAccount?.playerName?.toLowerCase().includes(q)
                || p.pickedSteamId.includes(q));
    }, [singlePicks, singleSearch]);

    const steamLogin = () => {
        window.location.href = `${baseURL}/Auth/steam`;
    };

    const renderBadges = (account: Account) => (
        <>
            {badges && fantasyWinnerSeasons(account.steamID, badges).map((season) => (
                <FantasyWinnerBadge key={season} season={season} />
            ))}
            {badges && isTopDonator(account.steamID, badges) && <TopDonatorBadge />}
            {badges && isVerifiedContent(account.steamID, badges) && <VerifiedBadge />}
            {account.premium && <PremiumBadge />}
            {account.donator && <DonatorBadge />}
        </>
    );


    return (
        <div className={styles.page}>
            {/* ===== Header ===== */}
            <div className={styles.pageHeader}>
                <div className={styles.headerTop}>
                    <div style={{ minWidth: 200 }}>
                        <h1 className={styles.title}>{t('pages:fantasy.title2')}</h1>
                        <p className={styles.subtitle}>{t('pages:fantasy.subtitle')}</p>
                    </div>
                    <div className={styles.headerActions}>
                        {visitor && myEntry && (
                            <Link to={`/fantasy/view/${visitor.steamID}/${season}`} className={styles.outlineButton}>
                                <Groups sx={{ fontSize: 18 }} />
                                {t('pages:fantasy.seeYourTeam')}
                            </Link>
                        )}
                        {visitor && (!isLocked || isAdmin) && (
                            <Link to="/fantasy/create" className={styles.primaryButton}>
                                <Edit sx={{ fontSize: 18 }} />
                                {t('pages:fantasy.createUpdateShort')}
                            </Link>
                        )}
                        {!visitor && !isLocked && (
                            <button className={styles.primaryButton} onClick={steamLogin}>
                                <Edit sx={{ fontSize: 18 }} />
                                {t('pages:fantasy.createUpdateShort')}
                            </button>
                        )}
                    </div>
                </div>

                <div className={styles.chipsRow}>
                    <span className={styles.seasonChip}>
                        <CalendarToday sx={{ fontSize: 16 }} />
                        {t('common:seasonNum', { number: season })}
                    </span>
                    {season === currentSeason && (
                        <span className={`${styles.statusChip} ${isLocked ? styles.statusChipLocked : styles.statusChipOpen}`}>
                            <span className={styles.statusDot} />
                            {isLocked ? t('pages:fantasy.statusLocked') : t('pages:fantasy.statusOpen')}
                        </span>
                    )}
                    {season === currentSeason && lock.loaded && lock.lockTime && (
                        <span
                            className={`${styles.deadlineChip} ${isLocked ? styles.deadlineChipClosed : lock.urgent ? styles.deadlineChipUrgent : ''}`}
                            title={t('pages:fantasy.lockDeadline', { date: lockDate })}
                        >
                            {isLocked
                                ? <LockOutlined sx={{ fontSize: 16 }} />
                                : <HourglassBottom sx={{ fontSize: 16 }} />}
                            {isLocked
                                ? t('pages:fantasy.lockedAt', { date: lockDate })
                                : t('pages:fantasy.locksIn', { time: countdown })}
                        </span>
                    )}
                    {myEntry && (
                        <span className={styles.placementChip}>
                            <span className={styles.placementLabel}>{t('pages:fantasy.yourPlacement')}</span>
                            <span className={styles.placementRank}>#{myEntry.entry.placement}</span>
                            <span className={styles.placementPoints}>{t('pages:fantasy.pointsCount', { count: myEntry.entry.points })}</span>
                            <span>· {getSimpleDivisionLabel(myEntry.division)} · {myEntry.entry.account.playerName}</span>
                        </span>
                    )}
                </div>
            </div>

            {/* ===== Tabs ===== */}
            <div className={styles.tabRow}>
                <button className={`${styles.tab} ${tab === 'leaderboard' ? styles.tabActive : ''}`} onClick={() => setTab('leaderboard')}>
                    <EmojiEvents sx={{ fontSize: 18 }} />
                    {t('pages:fantasy.tabLeaderboard')}
                </button>
                <button className={`${styles.tab} ${tab === 'picks' ? styles.tabActive : ''}`} onClick={() => setTab('picks')}>
                    <Groups sx={{ fontSize: 18 }} />
                    {t('pages:fantasy.tabPicks')}
                </button>
                <button className={`${styles.tab} ${tab === 'single' ? styles.tabActive : ''}`} onClick={() => setTab('single')}>
                    <MilitaryTech sx={{ fontSize: 18 }} />
                    {t('pages:fantasy.tabSingle')}
                </button>
            </div>

            {/* ===== Tab 1: Rangliste ===== */}
            {tab === 'leaderboard' && (
                <div className={styles.tabPanel}>
                    <div className={styles.sectionHeader}>
                        <div>
                            <h2 className={styles.sectionTitle}>{t('pages:fantasy.tabLeaderboard')}</h2>
                            <p className={styles.sectionDesc}>{t('pages:fantasy.leaderboardDesc', { season: t('common:seasonNum', { number: season }) })}</p>
                        </div>
                        <div className={styles.selectGroup}>
                            <div>
                                <label className={styles.selectLabel}>{t('common:selectSeason')}</label>
                                <select className={styles.select} value={season} onChange={(e) => setSeason(Number(e.target.value))}>
                                    {seasons.map((s) => (
                                        <option key={s} value={s}>{t('common:seasonNum', { number: s })}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className={styles.selectLabel}>{t('common:selectDivision')}</label>
                                <select className={styles.select} value={division ?? ''} onChange={(e) => setDivision(e.target.value)}>
                                    {divisions.map((d) => (
                                        <option key={d} value={d}>{getSimpleDivisionLabel(d)}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    <PrizePanel season={season} leaderboard={leaderboard} division={division} />

                    {loading ? (
                        <div className="text-center"><Loading /></div>
                    ) : entries.length === 0 ? (
                        <p className={styles.emptyState}>{t('pages:fantasy.noTeams')}</p>
                    ) : (
                        <div className={styles.table}>
                            <div className={`${styles.tableHeader} ${styles.gridLeaderboard}`}>
                                <div className={styles.cellCenter}>#</div>
                                <div>{t('common:name')}</div>
                                <div className={styles.cellRight}>{t('common:points')}</div>
                            </div>
                            {entries.map((entry, i) => (
                                <div className={`${styles.tableRow} ${styles.gridLeaderboard} ${rowClass(i)}`} key={entry.account.steamID}>
                                    <div className={styles.cellCenter}>
                                        <span className={`${styles.rankBadge} ${rankClass(entry.placement)}`}>{entry.placement}</span>
                                    </div>
                                    <div className={styles.nameCell}>
                                        <PlayerAvatar
                                            steamId={entry.account.steamID}
                                            name={entry.account.playerName}
                                            size={26}
                                            badges={badges}
                                        />
                                        <Link to={`/fantasy/view/${entry.account.steamID}/${season}/${division}`} className={styles.nameLink}>
                                            {entry.account.playerName}
                                        </Link>
                                        {renderBadges(entry.account)}
                                        {prizeByOwner.has(entry.account.steamID) && (
                                            <span className={styles.prizeChip} title={prizeByOwner.get(entry.account.steamID)}>
                                                <Redeem sx={{ fontSize: 13 }} />
                                                <span className={styles.prizeChipText}>{t('pages:fantasy.prizeChipLabel')}</span>
                                            </span>
                                        )}
                                        {entry.isCurrentUser && <span className={styles.youBadge}>{t('common:you')}</span>}
                                    </div>
                                    <div className={styles.pointsCell}>{entry.points}</div>
                                </div>
                            ))}
                        </div>
                    )}

                    {mostPicked.length > 0 && (
                        <>
                            <div className={styles.divider} />
                            <div className={styles.sectionHeader}>
                                <h2 className={styles.sectionTitle}>{t('pages:fantasy.mostPicked')}</h2>
                                <button className={styles.arrowLink} onClick={() => setTab('picks')}>
                                    {t('pages:fantasy.seeAllPicks')} <ArrowForward sx={{ fontSize: 18 }} />
                                </button>
                            </div>
                            <div className={styles.mostPickedGrid}>
                                {mostPicked.slice(0, 5).map((p, i) => (
                                    <Link to={`/player/${p.account.steamID}`} className={styles.pickCard} key={p.account.steamID}>
                                        <div className={styles.pickCardName}>{i + 1}. {p.account.playerName}</div>
                                        <div className={styles.pickCardPhoto}>
                                            <PlayerImage
                                                id={p.account.steamID}
                                                width={300}
                                                badges={badges ?? undefined}
                                                imgStyle={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top', display: 'block' }}
                                            />
                                        </div>
                                        <div className={styles.pickCardCount}><strong>{p.picks}</strong> {t('pages:fantasy.picks')}</div>
                                    </Link>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* ===== Tab 2: Mest valgte ===== */}
            {tab === 'picks' && (
                <div className={styles.tabPanel}>
                    <div className={styles.sectionHeader}>
                        <div>
                            <h2 className={styles.sectionTitle}>{t('pages:fantasy.mostPicked')}</h2>
                            <p className={styles.sectionDesc}>{t('pages:fantasy.picksDesc', { season: t('common:seasonNum', { number: season }) })}</p>
                        </div>
                    </div>
                    <div className={styles.controlsRow}>
                        <select className={styles.select} value={picksDivision} onChange={(e) => setPicksDivision(e.target.value)}>
                            <option value="all">{t('pages:fantasy.allDivisions')}</option>
                            {divisions.map((d) => (
                                <option key={d} value={d}>{getSimpleDivisionLabel(d)}</option>
                            ))}
                        </select>
                        <div className={styles.searchBox}>
                            <Search className={styles.searchIcon} sx={{ fontSize: 18 }} />
                            <input
                                className={styles.searchInput}
                                placeholder={t('pages:fantasy.searchPlaceholder')}
                                value={picksSearch}
                                onChange={(e) => setPicksSearch(e.target.value)}
                            />
                        </div>
                    </div>

                    {filteredPicks.length === 0 ? (
                        <p className={styles.emptyState}>{t('pages:fantasy.noPicksMatch')}</p>
                    ) : (
                        <div className={styles.table}>
                            <div className={`${styles.tableHeader} ${styles.gridPicks}`}>
                                <div className={styles.cellCenter}>#</div>
                                <div>{t('pages:fantasy.player')}</div>
                                <div className={styles.cellRight}>{t('pages:fantasy.pickCount')}</div>
                                <div className={styles.cellRight}>{t('pages:fantasy.share')}</div>
                            </div>
                            {filteredPicks.map((p, i) => (
                                <Link
                                    to={`/fantasy/teams-using-player/${p.account.steamID}/${season}`}
                                    className={`${styles.tableRow} ${styles.gridPicks} ${rowClass(i)}`}
                                    key={p.account.steamID}
                                >
                                    <div className={styles.cellCenter}>
                                        <span className={`${styles.rankBadge} ${rankClass(p.rank)}`}>{p.rank}</span>
                                    </div>
                                    <div className={styles.nameCell}>
                                        <PlayerAvatar
                                            steamId={p.account.steamID}
                                            name={p.account.playerName}
                                            size={26}
                                            badges={badges}
                                        />
                                        <span className={styles.nameLink}>{p.account.playerName}</span>
                                    </div>
                                    <div className={styles.pointsCell}>{p.picks}</div>
                                    <div className={styles.pctCell}>{p.pct}%</div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* ===== Tab 3: Bedste enkeltvalg ===== */}
            {tab === 'single' && (
                <div className={styles.tabPanel}>
                    <div className={styles.sectionHeader}>
                        <div>
                            <h2 className={styles.sectionTitle}>{t('pages:fantasy.tabSingle')}</h2>
                            <p className={styles.sectionDesc}>{t('pages:fantasy.singleDesc', { season: t('common:seasonNum', { number: season }) })}</p>
                        </div>
                    </div>
                    <div className={styles.controlsRow}>
                        <select className={styles.select} value={singleDivision ?? ''} onChange={(e) => setSingleDivision(e.target.value)}>
                            {divisions.map((d) => (
                                <option key={d} value={d}>{getSimpleDivisionLabel(d)}</option>
                            ))}
                        </select>
                        <div className={styles.searchBox}>
                            <Search className={styles.searchIcon} sx={{ fontSize: 18 }} />
                            <input
                                className={styles.searchInput}
                                placeholder={t('pages:fantasy.searchPlaceholder')}
                                value={singleSearch}
                                onChange={(e) => setSingleSearch(e.target.value)}
                            />
                        </div>
                    </div>

                    {filteredSingle.length === 0 ? (
                        <p className={styles.emptyState}>
                            {t('pages:fantasy.noSingle', { division: singleDivision ? getSimpleDivisionLabel(singleDivision) : '', season: t('common:seasonNum', { number: season }) })}
                        </p>
                    ) : (
                        <div className={styles.table}>
                            <div className={`${styles.tableHeader} ${styles.gridSingle}`}>
                                <div className={styles.cellCenter}>#</div>
                                <div>{t('pages:fantasy.player')}</div>
                                <div className={styles.cellRight}>{t('pages:fantasy.totalPoints')}</div>
                                <div className={styles.gridSingleHideSm}>{t('pages:fantasy.owner')}</div>
                                <div className={styles.gridSingleHideSm}></div>
                            </div>
                            {filteredSingle.map((p, i) => (
                                <div className={`${styles.tableRow} ${styles.gridSingle} ${rowClass(i)}`} key={`${p.pickedSteamId}-${p.fantasyTeamId}`}>
                                    <div className={styles.cellCenter}>
                                        <span className={`${styles.rankBadge} ${rankClass(p.rank)}`}>{p.rank}</span>
                                    </div>
                                    <div className={styles.nameCell}>
                                        <PlayerAvatar
                                            steamId={p.pickedSteamId}
                                            name={p.playerAccount?.playerName}
                                            size={26}
                                            badges={badges}
                                        />
                                        <Link to={`/player/${p.pickedSteamId}`} className={styles.nameLink}>
                                            {p.playerAccount?.playerName ?? p.pickedSteamId}
                                        </Link>
                                    </div>
                                    <div className={styles.bigPointsCell}>{p.totalPoints}</div>
                                    <div className={`${styles.ownerCell} ${styles.gridSingleHideSm}`}>
                                        <PlayerAvatar
                                            steamId={p.ownerSteamId}
                                            name={p.ownerAccount?.playerName}
                                            size={20}
                                            badges={badges}
                                        />
                                        <span className={styles.ownerLabel}>{t('pages:fantasy.ownedBy')}</span>
                                        <Link
                                            to={`/fantasy/view/${p.ownerSteamId}/${season}/${singleDivision}`}
                                            className={styles.ownerLink}
                                        >
                                            {p.ownerAccount?.playerName ?? p.ownerSteamId}
                                        </Link>
                                    </div>
                                    <div className={`${styles.cellRight} ${styles.gridSingleHideSm}`}>
                                        <Link
                                            to={`/fantasy/view/${p.ownerSteamId}/${season}/${singleDivision}`}
                                            className={styles.arrowLink}
                                        >
                                            {t('pages:fantasy.seeTeam')} <ArrowForward sx={{ fontSize: 16 }} />
                                        </Link>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default FantasyPage2;
